// src/features/dzban/hooks/useDzbanVoting.js
// Hook for Dżban Roku voting functionality

import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, getDoc, doc, addDoc, query, where, limit, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useVoterFingerprint } from './useVoterFingerprint';

export const useDzbanVoting = () => {
  const [randomSpots, setRandomSpots] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(false);
  const { voterId, votesCount, canVote, maxVotes, incrementVoteCount } = useVoterFingerprint();

  // Fetch 2 random verified spots for voting
  const fetchRandomSpots = useCallback(async () => {
    try {
      setLoading(true);
      
      // Query verified spots (status = "verified")
      const q = query(
        collection(db, 'sightings'),
        where('status', '==', 'verified'),
        limit(100)
      );
      
      const querySnapshot = await getDocs(q);
      const spots = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter spots with images
      const spotsWithImages = spots.filter(spot => 
        (spot.images && spot.images.length > 0) || spot.imageUrl
      );

      if (spotsWithImages.length < 2) {
        console.warn('Not enough spots for voting');
        setRandomSpots([]);
        return;
      }

      // Pick 2 random spots
      const shuffled = [...spotsWithImages].sort(() => 0.5 - Math.random());
      const selected = shuffled.slice(0, 2);
      
      setRandomSpots(selected);
    } catch (error) {
      console.error('Error fetching random spots:', error);
      setRandomSpots([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Submit vote
  const vote = async (winnerId, loserId) => {
    if (!canVote) {
      console.warn('Vote limit reached');
      return false;
    }

    try {
      // Add vote to Firestore
      await addDoc(collection(db, 'dzban_votes'), {
        winnerId,
        loserId,
        voterId,
        timestamp: serverTimestamp()
      });

      // Increment local vote count
      incrementVoteCount();

      // Fetch new random spots
      await fetchRandomSpots();

      return true;
    } catch (error) {
      console.error('Error submitting vote:', error);
      return false;
    }
  };

  // Calculate ranking from votes
  const fetchRanking = async () => {
    try {
      // Fetch all votes
      const votesSnapshot = await getDocs(collection(db, 'dzban_votes'));
      const votes = votesSnapshot.docs.map(doc => doc.data());

      // Count wins for each sighting
      const winCounts = {};
      
      votes.forEach(vote => {
        winCounts[vote.winnerId] = (winCounts[vote.winnerId] || 0) + 1;
      });

      // Fetch spot details for top winners
      const topWinnerIds = Object.entries(winCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([id]) => id);

      // Fetch spot details
      const rankingData = [];
      for (const spotId of topWinnerIds) {
        try {
          const spotDoc = await getDoc(doc(db, 'sightings', spotId));
          
          if (spotDoc.exists()) {
            rankingData.push({
              id: spotDoc.id,
              ...spotDoc.data(),
              votes: winCounts[spotId]
            });
          }
        } catch (err) {
          console.error('Error fetching spot:', err);
        }
      }

      setRanking(rankingData);
    } catch (error) {
      console.error('Error fetching ranking:', error);
    }
  };

  // NEW: Fetch city ranking (TOP 20 cities)
  const fetchCityRanking = async () => {
    try {
      // Fetch all votes
      const votesSnapshot = await getDocs(collection(db, 'dzban_votes'));
      const votes = votesSnapshot.docs.map(doc => doc.data());

      // Count wins for each sighting
      const winCounts = {};
      votes.forEach(vote => {
        winCounts[vote.winnerId] = (winCounts[vote.winnerId] || 0) + 1;
      });

      // Fetch all verified spots (status = "verified")
      const q = query(
        collection(db, 'sightings'),
        where('status', '==', 'verified'),
        limit(100)
      );
      const spotsSnapshot = await getDocs(q);
      
      // Aggregate by city
      const cityData = {};
      
      spotsSnapshot.docs.forEach(doc => {
        const spot = { id: doc.id, ...doc.data() };
        const voteCount = winCounts[doc.id] || 0;
        
        // DEBUG
        if (voteCount > 0) {
          console.log('🔍 Spot z głosami:', {
            id: doc.id,
            plate: spot.plate,
            locationName: spot.locationName,
            voteCount: voteCount
          });
        }
        
        if (voteCount > 0) {
          const city = extractCity(spot);
          
          // DEBUG
          console.log('   → Miasto:', city);
          
          if (!cityData[city]) {
            cityData[city] = {
              name: city,
              totalVotes: 0,
              spotCount: 0,
              topSpots: []
            };
          }
          
          cityData[city].totalVotes += voteCount;
          cityData[city].spotCount += 1;
          cityData[city].topSpots.push({ ...spot, votes: voteCount });
        }
      });
      
      // Sort top spots within each city
      Object.values(cityData).forEach(city => {
        city.topSpots.sort((a, b) => b.votes - a.votes);
        city.topSpots = city.topSpots.slice(0, 3); // Keep top 3
      });
      
      // Create ranking array
      const cityRanking = Object.values(cityData)
        .sort((a, b) => b.totalVotes - a.totalVotes);
      
      return cityRanking;
    } catch (error) {
      console.error('Error fetching city ranking:', error);
      return [];
    }
  };

  // Fetch brand ranking (similar to city ranking)
  const fetchBrandRanking = async () => {
    try {
      // Fetch all votes
      const votesSnapshot = await getDocs(collection(db, 'dzban_votes'));
      const votes = votesSnapshot.docs.map(doc => doc.data());

      // Count wins for each sighting
      const winCounts = {};
      votes.forEach(vote => {
        winCounts[vote.winnerId] = (winCounts[vote.winnerId] || 0) + 1;
      });

      // Fetch all verified spots (status = "verified")
      const q = query(
        collection(db, 'sightings'),
        where('status', '==', 'verified'),
        limit(100)
      );
      const spotsSnapshot = await getDocs(q);
      
      // Aggregate by brand
      const brandData = {};
      
      spotsSnapshot.docs.forEach(doc => {
        const spot = { id: doc.id, ...doc.data() };
        const voteCount = winCounts[doc.id] || 0;
        
        if (voteCount > 0) {
          const brand = extractBrand(spot);
          
          if (!brandData[brand]) {
            brandData[brand] = {
              name: brand,
              totalVotes: 0,
              spotCount: 0,
              topSpots: []
            };
          }
          
          brandData[brand].totalVotes += voteCount;
          brandData[brand].spotCount += 1;
          brandData[brand].topSpots.push({ ...spot, votes: voteCount });
        }
      });
      
      // Sort top spots within each brand
      Object.values(brandData).forEach(brand => {
        brand.topSpots.sort((a, b) => b.votes - a.votes);
        brand.topSpots = brand.topSpots.slice(0, 3); // Keep top 3
      });
      
      // Create ranking array
      const brandRanking = Object.values(brandData)
        .sort((a, b) => b.totalVotes - a.totalVotes);
      
      return brandRanking;
    } catch (error) {
      console.error('Error fetching brand ranking:', error);
      return [];
    }
  };

  // Helper: Extract and normalize brand name
  const extractBrand = (spot) => {
    if (!spot.brand) return 'Inne marki';
    
    const brand = spot.brand.toUpperCase().trim();
    
    // Normalize common variations
    const brandMap = {
      'BMW': ['BMW', 'B M W', 'B.M.W'],
      'AUDI': ['AUDI'],
      'MERCEDES': ['MERCEDES', 'MERCEDES-BENZ', 'MERCEDES BENZ', 'MB'],
      'VOLKSWAGEN': ['VOLKSWAGEN', 'VW', 'V.W'],
      'TOYOTA': ['TOYOTA'],
      'SKODA': ['SKODA', 'ŠKODA'],
      'FORD': ['FORD'],
      'OPEL': ['OPEL'],
      'RENAULT': ['RENAULT'],
      'PEUGEOT': ['PEUGEOT'],
      'CITROEN': ['CITROEN', 'CITROËN'],
      'FIAT': ['FIAT'],
      'KIA': ['KIA'],
      'HYUNDAI': ['HYUNDAI'],
      'NISSAN': ['NISSAN'],
      'MAZDA': ['MAZDA'],
      'HONDA': ['HONDA'],
      'VOLVO': ['VOLVO'],
      'SEAT': ['SEAT'],
      'DACIA': ['DACIA']
    };
    
    // Try to match
    for (const [standardName, variants] of Object.entries(brandMap)) {
      if (variants.includes(brand)) {
        return standardName;
      }
    }
    
    // Return as-is if not in map
    return brand;
  };

  // Helper: Extract city from location string
  const extractCity = (spot) => {
    const cityMap = {
      // Nazwa z ogonkami: [wersja z ogonkami, wersja bez ogonków]
      'warszawa': ['warszawa', 'warszawa'],
      'kraków': ['kraków', 'krakow'],
      'wrocław': ['wrocław', 'wroclaw'],
      'poznań': ['poznań', 'poznan'],
      'gdańsk': ['gdańsk', 'gdansk'],
      'szczecin': ['szczecin', 'szczecin'],
      'łódź': ['łódź', 'lodz'],
      'bydgoszcz': ['bydgoszcz', 'bydgoszcz'],
      'lublin': ['lublin', 'lublin'],
      'katowice': ['katowice', 'katowice'],
      'białystok': ['białystok', 'bialystok'],
      'gdynia': ['gdynia', 'gdynia'],
      'częstochowa': ['częstochowa', 'czestochowa'],
      'radom': ['radom', 'radom'],
      'sosnowiec': ['sosnowiec', 'sosnowiec'],
      'toruń': ['toruń', 'torun'],
      'kielce': ['kielce', 'kielce'],
      'rzeszów': ['rzeszów', 'rzeszow'],
      'gliwice': ['gliwice', 'gliwice'],
      'zabrze': ['zabrze', 'zabrze']
    };
    
    // Try multiple fields
    const locationFields = [
      spot.locationName,
      spot.location,
      spot.address,
      spot.city
    ].filter(Boolean).join(' ');
    
    const lower = locationFields.toLowerCase();
    
    // Try to find city name (with or without Polish characters)
    for (const [cityName, variants] of Object.entries(cityMap)) {
      for (const variant of variants) {
        if (lower.includes(variant)) {
          return capitalizeCity(cityName);
        }
      }
    }
    
    return 'Inne miasta';
  };

  const capitalizeCity = (city) => {
    return city.charAt(0).toUpperCase() + city.slice(1);
  };

  // Auto-fetch random spots on mount - DISABLED (manual fetch from DzbanHome)
  // useEffect(() => {
  //   fetchRandomSpots();
  // }, [fetchRandomSpots]);

  return {
    randomSpots,
    ranking,
    loading,
    vote,
    fetchRanking,
    fetchRandomSpots, // Export for manual use
    fetchCityRanking, // Changed from fetchVoivodeshipRanking
    fetchBrandRanking, // NEW - Brand ranking
    votesCount,
    canVote,
    maxVotes
  };
};
